import {
  MEMORY_TEXT_MAX_CHARS,
  type MemoryKind,
  type MemoryList,
  type MemoryView,
} from "@esi/shared";
import {
  Download,
  Ellipsis,
  EyeOff,
  Folder,
  Hourglass,
  Pencil,
  Plus,
  Search,
  Trash2,
  TriangleAlert,
  Users,
} from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppDialog } from "@/components/AppDialog";
import { FilterChips } from "@/components/FilterChips";
import { InlineError, Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { ApiError } from "../api-errors.ts";
import { formatDate } from "../i18n/format.ts";
import {
  useMemories,
  useMemoryExport,
  useMemoryMutations,
  useProjectMemoryMutations,
} from "./queries.ts";

type Translate = (
  key: "memory.proposal.invalid" | "memory.panel.kindLocked" | "errors.network",
) => string;

/** A failed memory write in plain words: the server's refusal, or the network. */
function failure(error: unknown, t: Translate): string {
  if (error instanceof ApiError && error.status === 422) {
    return t("memory.proposal.invalid");
  }
  if (error instanceof ApiError && error.code === "memory_kind") {
    return t("memory.panel.kindLocked");
  }
  return error instanceof ApiError ? error.message : t("errors.network");
}

/**
 * The memory panel of account settings (openspec change `memory-personal`): the user's
 * preferences and facts, searchable, each editable and deletable, plus "delete all". A fact the
 * user can no longer read the sources of stays as a hidden row they can delete. When an admin
 * turned memory off, the list stays and nothing can be added.
 */
export function MemorySection() {
  const { t } = useTranslation();
  const id = useId();
  const [query, setQuery] = useState("");
  const [kind, setKind] = useState<MemoryKind>("preference");
  const [adding, setAdding] = useState(false);
  const [confirmAll, setConfirmAll] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  // The list follows the search once typing pauses.
  const [settled, setSettled] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setSettled(query), 250);
    return () => clearTimeout(timer);
  }, [query]);
  const memories = useMemories(settled);
  const writes = useMemoryMutations();
  const exporting = useMemoryExport();
  const addButton = useRef<HTMLButtonElement>(null);
  const lastData = useRef<MemoryList | null>(null);

  // A failed search keeps the last list and its controls, so the query can be changed.
  const shownData = memories.data ?? lastData.current;
  if (memories.data) {
    lastData.current = memories.data;
  }
  if (memories.isError && !shownData) {
    return <Notice tone="danger">{failure(memories.error, t)}</Notice>;
  }
  if (!shownData) {
    return <Skeleton className="h-40 w-full" />;
  }
  const { items, locked } = shownData;
  const shown = items.filter((m) => m.kind === kind);
  const count = (k: MemoryKind) => items.filter((m) => m.kind === k).length;

  return (
    <div data-esi-content="memory" className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <p className="max-w-prose text-sm leading-relaxed text-fg-secondary">
          {t("memory.panel.lead")}
        </p>
        {locked ? null : (
          <Button
            ref={addButton}
            type="button"
            size="sm"
            onClick={() => {
              writes.add.reset();
              setAdding(true);
            }}
          >
            <Plus aria-hidden="true" strokeWidth={1.75} />
            {t("memory.panel.add")}
          </Button>
        )}
      </div>
      {locked ? <Notice>{t("memory.panel.locked")}</Notice> : null}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <FilterChips
          label={t("memory.panel.filter")}
          value={kind}
          onChange={setKind}
          options={[
            {
              value: "preference",
              label: t("memory.panel.preferences"),
              count: count("preference"),
            },
            { value: "fact", label: t("memory.panel.facts"), count: count("fact") },
          ]}
        />
        <div className="relative w-full sm:max-w-60">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-fg-muted"
            strokeWidth={1.75}
          />
          <Input
            type="search"
            aria-label={t("memory.panel.search")}
            placeholder={t("memory.panel.search")}
            value={query}
            maxLength={200}
            onChange={(e) => setQuery(e.currentTarget.value)}
            className="h-8 pl-8 text-[13px]"
          />
        </div>
      </div>
      {memories.isError ? <InlineError>{failure(memories.error, t)}</InlineError> : null}
      {shown.length === 0 ? (
        <p className="rounded-lg border border-dashed border-line px-4 py-6 text-center text-sm text-fg-muted">
          {query.trim()
            ? t("memory.panel.noMatch")
            : kind === "preference"
              ? t("memory.panel.emptyPreferences")
              : t("memory.panel.emptyFacts")}
        </p>
      ) : (
        <ul
          aria-label={
            kind === "preference" ? t("memory.panel.preferences") : t("memory.panel.facts")
          }
          className="flex flex-col divide-y divide-line rounded-lg border border-line bg-surface-raised"
        >
          {shown.map((memory) => (
            <MemoryRow key={memory.id} memory={memory} onDone={(message) => setStatus(message)} />
          ))}
        </ul>
      )}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="-ml-2"
          disabled={exporting.isPending}
          onClick={() =>
            exporting.mutate(undefined, {
              onSuccess: (file) => setStatus(t("memory.panel.exported", { file })),
            })
          }
        >
          <Download aria-hidden="true" strokeWidth={1.75} />
          {exporting.isPending ? t("memory.panel.exporting") : t("memory.panel.export")}
        </Button>
        {items.length > 0 ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-danger-fg hover:text-danger-fg"
            onClick={() => {
              writes.removeAll.reset();
              setConfirmAll(true);
            }}
          >
            <Trash2 aria-hidden="true" strokeWidth={1.75} />
            {t("memory.panel.deleteAll")}
          </Button>
        ) : null}
      </div>
      {exporting.isError ? <InlineError>{t("memory.panel.exportFailed")}</InlineError> : null}
      <p role="status" className="sr-only">
        {status}
      </p>
      <AppDialog
        open={adding}
        onClose={() => setAdding(false)}
        title={t("memory.panel.addTitle")}
        description={t("memory.panel.addDescription")}
        returnFocus={addButton.current}
      >
        <MemoryForm
          id={`${id}-add`}
          initialKind={kind}
          busy={writes.add.isPending}
          error={writes.add.error ? failure(writes.add.error, t) : null}
          onCancel={() => setAdding(false)}
          onSubmit={(input) =>
            writes.add.mutate(input, {
              onSuccess: () => {
                setAdding(false);
                setKind(input.kind);
                setStatus(t("memory.panel.saved"));
              },
            })
          }
        />
      </AppDialog>
      <AppDialog
        open={confirmAll}
        onClose={() => setConfirmAll(false)}
        title={t("memory.panel.deleteAllTitle")}
      >
        <ul className="flex list-disc flex-col gap-1.5 pl-5 text-sm text-fg-secondary">
          <li>{t("memory.panel.deleteAllEffects.everything")}</li>
          <li>{t("memory.panel.deleteAllEffects.nextTurn")}</li>
          <li>{t("memory.panel.deleteAllEffects.kept")}</li>
        </ul>
        {writes.removeAll.error ? (
          <InlineError>{failure(writes.removeAll.error, t)}</InlineError>
        ) : null}
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => setConfirmAll(false)}>
            {t("memory.proposal.cancel")}
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={writes.removeAll.isPending}
            onClick={() =>
              writes.removeAll.mutate(undefined, {
                onSuccess: ({ deleted }) => {
                  setConfirmAll(false);
                  setStatus(t("memory.panel.allDeleted", { count: deleted }));
                },
              })
            }
          >
            {writes.removeAll.isPending
              ? t("memory.panel.deleting")
              : t("memory.panel.confirmDeleteAll")}
          </Button>
        </DialogFooter>
      </AppDialog>
    </div>
  );
}

/** The kind (when adding) and the text, with the server's limit counted down. */
export function MemoryForm(props: {
  id: string;
  initialKind?: MemoryKind;
  initialText?: string;
  /** Adding: the kind is chosen here; editing keeps it. */
  withKind?: boolean;
  busy: boolean;
  error: string | null;
  /** The submit button's label (default "Save"). */
  submitLabel?: string;
  onCancel: () => void;
  onSubmit: (input: { kind: MemoryKind; text: string }) => void;
}) {
  const { t } = useTranslation();
  const [kind, setKind] = useState<MemoryKind>(props.initialKind ?? "preference");
  const [text, setText] = useState(props.initialText ?? "");
  const trimmed = text.trim();
  const tooLong = trimmed.length > MEMORY_TEXT_MAX_CHARS;
  const adding = props.withKind ?? props.initialText === undefined;
  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (trimmed && !tooLong) {
          props.onSubmit({ kind, text: trimmed });
        }
      }}
    >
      {adding ? (
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-2 text-sm font-medium text-fg">{t("memory.panel.kind")}</legend>
          <RadioGroup value={kind} onValueChange={(value) => setKind(value as MemoryKind)}>
            {(["preference", "fact"] as const).map((k) => (
              <div key={k} className="flex items-start gap-2">
                <RadioGroupItem id={`${props.id}-${k}`} value={k} className="mt-0.5" />
                <Label
                  htmlFor={`${props.id}-${k}`}
                  className="flex flex-col items-start gap-0.5 font-normal"
                >
                  <span className="text-fg">
                    {k === "preference" ? t("memory.panel.preferences") : t("memory.panel.facts")}
                  </span>
                  <span className="text-xs text-fg-muted">
                    {k === "preference"
                      ? t("memory.panel.preferenceHint")
                      : t("memory.panel.factHint")}
                  </span>
                </Label>
              </div>
            ))}
          </RadioGroup>
        </fieldset>
      ) : null}
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${props.id}-text`} className={adding ? undefined : "sr-only"}>
          {t("memory.panel.text")}
        </Label>
        <Textarea
          id={`${props.id}-text`}
          rows={2}
          autoFocus
          value={text}
          aria-invalid={tooLong || undefined}
          aria-describedby={`${props.id}-count`}
          // Editing continues the text: the caret starts at its end.
          onFocus={(e) => {
            const end = e.currentTarget.value.length;
            e.currentTarget.setSelectionRange(end, end);
          }}
          onChange={(e) => setText(e.currentTarget.value)}
          className="resize-none text-sm"
        />
        <span
          id={`${props.id}-count`}
          className={cn("text-xs tabular-nums", tooLong ? "text-danger-fg" : "text-fg-muted")}
        >
          {t("memory.proposal.remaining", { count: MEMORY_TEXT_MAX_CHARS - trimmed.length })}
        </span>
      </div>
      {props.error ? <InlineError>{props.error}</InlineError> : null}
      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          size={adding ? "default" : "sm"}
          onClick={props.onCancel}
        >
          {t("memory.proposal.cancel")}
        </Button>
        <Button
          type="submit"
          size={adding ? "default" : "sm"}
          disabled={props.busy || !trimmed || tooLong}
        >
          {props.busy
            ? t("memory.proposal.saving")
            : (props.submitLabel ?? t("memory.proposal.save"))}
        </Button>
      </div>
    </form>
  );
}

function MemoryRow(props: { memory: MemoryView; onDone: (message: string) => void }) {
  const { memory } = props;
  const { t } = useTranslation();
  const id = useId();
  const { edit, remove } = useMemoryMutations();
  // A fact of a project the user may suggest to (memory-project): copied, the fact stays.
  const { suggest } = useProjectMemoryMutations(memory.origin?.projectId ?? "");
  const [editing, setEditing] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const deleteIt = () =>
    remove.mutate(memory.id, { onSuccess: () => props.onDone(t("memory.panel.deleted")) });

  if (memory.hidden) {
    return (
      <li className="flex items-center gap-3 px-3.5 py-3">
        <EyeOff aria-hidden="true" className="size-4 shrink-0 text-fg-muted" strokeWidth={1.75} />
        <p className="min-w-0 flex-1 text-sm text-fg-secondary">{t("memory.panel.hidden")}</p>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={remove.isPending}
          onClick={deleteIt}
          aria-label={t("memory.panel.deleteHidden")}
        >
          {t("memory.panel.delete")}
        </Button>
      </li>
    );
  }

  const project =
    memory.kind === "fact"
      ? memory.origin
        ? (memory.origin.name ?? t("memory.panel.unknownProject"))
        : t("memory.panel.noProject")
      : null;

  return (
    <li className="flex flex-col gap-2 px-3.5 py-3">
      {editing ? (
        <MemoryForm
          id={id}
          initialKind={memory.kind}
          initialText={memory.text ?? ""}
          withKind={false}
          busy={edit.isPending}
          error={edit.error ? failure(edit.error, t) : null}
          onCancel={() => {
            edit.reset();
            setEditing(false);
            trigger.current?.focus();
          }}
          onSubmit={({ text }) =>
            edit.mutate(
              { id: memory.id, text },
              {
                onSuccess: () => {
                  setEditing(false);
                  props.onDone(t("memory.panel.saved"));
                },
              },
            )
          }
        />
      ) : (
        <div className="flex items-start gap-3">
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <p className="text-sm leading-relaxed break-words text-fg">{memory.text}</p>
            <p className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-fg-muted">
              {project ? (
                <>
                  <span className="inline-flex min-w-0 items-center gap-1">
                    <Folder aria-hidden="true" className="size-3 shrink-0" strokeWidth={1.75} />
                    <span className="truncate">{project}</span>
                  </span>
                  <span aria-hidden="true">·</span>
                </>
              ) : null}
              {memory.trust === "external" ? (
                <>
                  <span className="inline-flex items-center gap-1 text-warning-fg">
                    <TriangleAlert aria-hidden="true" className="size-3" strokeWidth={1.75} />
                    {t("memory.panel.unverified")}
                  </span>
                  <span aria-hidden="true">·</span>
                </>
              ) : null}
              <span className="tabular-nums">
                {memory.lastUsedAt
                  ? t("memory.panel.lastUsed", { date: formatDate(memory.lastUsedAt) })
                  : t("memory.panel.neverUsed")}
              </span>
              {memory.expiresAt ? (
                <>
                  <span aria-hidden="true">·</span>
                  <span className="inline-flex items-center gap-1 text-warning-fg tabular-nums">
                    <Hourglass aria-hidden="true" className="size-3" strokeWidth={1.75} />
                    {t("memory.panel.expiring", { date: formatDate(memory.expiresAt) })}
                  </span>
                </>
              ) : null}
            </p>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                ref={trigger}
                variant="ghost"
                size="icon-sm"
                aria-label={t("memory.panel.actions")}
                className="-my-1 shrink-0 text-fg-muted hover:text-fg"
              >
                <Ellipsis aria-hidden="true" strokeWidth={1.75} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-40">
              <DropdownMenuItem
                onSelect={() => {
                  edit.reset();
                  setEditing(true);
                }}
              >
                <Pencil aria-hidden="true" strokeWidth={1.75} />
                {t("memory.panel.edit")}
              </DropdownMenuItem>
              {memory.suggestable ? (
                <DropdownMenuItem
                  disabled={suggest.isPending}
                  onSelect={() =>
                    suggest.mutate(
                      { memoryId: memory.id },
                      {
                        onSuccess: (view) =>
                          props.onDone(
                            view.status === "active"
                              ? t("memory.proposal.addedProject", {
                                  project: memory.origin?.name ?? t("memory.proposal.thisProject"),
                                })
                              : t("memory.panel.suggestedProject"),
                          ),
                      },
                    )
                  }
                >
                  <Users aria-hidden="true" strokeWidth={1.75} />
                  {t("memory.panel.suggest")}
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuItem variant="destructive" onSelect={deleteIt}>
                <Trash2 aria-hidden="true" strokeWidth={1.75} />
                {t("memory.panel.delete")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
      {remove.error ? <InlineError>{failure(remove.error, t)}</InlineError> : null}
      {suggest.error ? (
        <InlineError>
          {suggest.error instanceof ApiError && suggest.error.code === "memory_queue_full"
            ? t("memory.panel.queueFull")
            : suggest.error instanceof ApiError && suggest.error.code === "memory_duplicate"
              ? t("memory.panel.duplicate")
              : failure(suggest.error, t)}
        </InlineError>
      ) : null}
    </li>
  );
}
